import FirebaseService from '@/services/FirebaseService'
import { collection, DocumentData, FirestoreDataConverter, getDocs, limit, orderBy, query } from 'firebase/firestore'

export const BILL_STATUS = {
  OUTSTANDING: 'outstanding',
  OVERDUE: 'overdue',
  PAID: 'paid'
}

export class BillPayment {
  id: string
  date: Date
  method: string
  amount: number
  memo: string

  constructor() {
    this.date = new Date()
    this.method = ''
    this.amount = 0
    this.memo = ''
  }
}

export interface SyncMetadata {
  qboId?: string
  lastSynced?: number
  lastSyncStatus?: 'success' | 'error' | 'conflict'
  lastSyncError?: string
  conflictData?: any
  syncToken?: string
}

export class BillItem {
  id: string
  purchaseOrderId: string
  workOrderId: string
  name: string
  quantity: number
  unitPrice: number
  total: number
  projectId: string

  constructor () {
    this.name = ''
    this.quantity = 0
    this.unitPrice = 0
    this.total = 0
  }
}

/**
 * Bill class
 *
 * Bills are payment we have to make to our vendor for purchase order
 * and subcontractors for work orders
 */
export class Bill {
  id: string
  status: string
  dueDate: Date
  billDate: Date
  balance: number
  total: number
  payee: string
  purchaseOrderId: string
  workOrderId: string
  billNumber: string
  projectId: string
  items: BillItem[]
  attachments: string[]
  payments: BillPayment[]
  syncMeta?: SyncMetadata
  createdAt: Date
  updatedAt: Date

  constructor() {
    this.billNumber = ''
    this.payee = ''
    this.status = BILL_STATUS.OUTSTANDING
    this.balance = 0
    this.dueDate = new Date()
    this.billDate = new Date()
    this.total = 0
    this.items = []
    this.attachments = []
    this.payments = []
    this.createdAt = new Date()
    this.updatedAt = new Date()
  }
}

export const billConverter: FirestoreDataConverter<Bill, DocumentData> = {
  toFirestore: (bill: Bill) => {
    bill.createdAt = bill.createdAt ? new Date(bill.createdAt) : new Date()
    bill.updatedAt = new Date()
    return Object.assign({}, JSON.parse(JSON.stringify(bill)))
  },

  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)
    data.dueDate = (typeof data.dueDate === 'object') ? data.dueDate.toDate() : data.dueDate
    data.billDate = (typeof data.billDate === 'object') ? data.billDate.toDate() : data.billDate
    data.createdAt = (typeof data.createdAt === 'object') ? data.createdAt.toDate() : data.createdAt
    data.updatedAt = (typeof data.updatedAt === 'object') ? data.updatedAt.toDate() : data.updatedAt
    return Object.assign(new Bill(), JSON.parse(JSON.stringify(data))) as Bill
  }
}

export const billPaymentConverter = {
  toFirestore: (billPayment: BillPayment) => {
    return Object.assign({}, JSON.parse(JSON.stringify(billPayment)))
  },

  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)
    data.date = (typeof data.date === 'object') ? data.date.toDate() : data.date

    return Object.assign(new BillPayment(), JSON.parse(JSON.stringify(data))) as BillPayment
  }
}

const getLastBillId = async () => {
  const db = await FirebaseService.firestore()
  const collectionRef = await collection(db, 'bills')
  const queryRef = query(collectionRef, orderBy('billNumber', 'desc'), limit(1))
  const querySnapshot = await getDocs(queryRef)

  if (querySnapshot.empty) return '1000'

  const lastBill = querySnapshot.docs[0].data()
  return lastBill.billNumber.substring(6) || '1000'
}

export const generateBillId = async () => {
  const lastBillId = await getLastBillId()
  const label = 'BILL'
  const year = new Date().getFullYear().toString().substring(2)
  const counter = parseInt(lastBillId)
  return `${label}${year}${counter + 1}`
}
